import {
  Button,
  ChevronDownIcon,
  DebouncedTextField,
  FileDrop,
  Flex,
  FunnelIcon,
  Grid,
  IconButton,
  LoaderCover,
  MagnifyingGlassIcon,
  Menu,
  PlusIcon,
  Text,
  ViewSelector,
  ViewType,
} from 'components'
import { useAlert, useWorkspace } from 'components/providers'
import { startCase } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { FirestoreFilter, IMedia, MediaType } from 'types'
import { useLocalStorage } from 'usehooks-ts'
import { acceptedMediaFiles, deleteDoc, getDocs, useBreakpoint, useHotKey } from 'utils'
import MediaDialog from './MediaDialog'
import MediaGrid from './MediaGrid'

export interface Props {
  onSelect?: (id: string) => void
  noCreate?: boolean
  onRemove?: (id: string) => void
  type?: MediaType
}

interface Media {
  id: string
  title: string
  type: MediaType
}

export default function ManageMedia({ onSelect, noCreate, onRemove, type }: Props) {
  const { confirm } = useAlert()
  const { workspace } = useWorkspace()
  const [loading, setLoading] = useState(false)
  const [media, setMedia] = useState<Media[]>([])
  const [newMedia, setNewMedia] = useState<File>()
  const [search, setSearch] = useState('')
  const [mediaIds, setMediaIds] = useState<string[]>([])
  const [typeFilter, setTypeFilter] = useState<string>(type || 'all')
  const [view, setView] = useLocalStorage<ViewType>('content-display-mode', 'grid')
  const isMobile = useBreakpoint() < 2

  const workspaceId = workspace?.id
  const searchRef = useRef<HTMLInputElement>(null)
  useHotKey({ key: 'f', options: { ctrlKey: true } }, () => searchRef.current?.focus())

  useEffect(() => {
    if (!workspaceId) return

    setLoading(true)
    const constraints: FirestoreFilter<IMedia>[] = []
    if (type) constraints.push({ field: 'type', op: '==', value: type })
    if (workspaceId) constraints.push({ field: 'workspaceId', op: '==', value: workspaceId })
    getDocs({ collection: 'Media', constraints: constraints, fields: ['title', 'type'] }).then(
      res => {
        setMedia(res.data.map(doc => ({ id: doc.id, title: doc.title, type: doc.type })))
        setLoading(false)
      }
    )
  }, [workspaceId])

  useEffect(() => {
    if (!media.length) return
    const searchedMedia = media.filter(m => m.title.toLowerCase().includes(search.toLowerCase()))
    const filteredMedia =
      typeFilter === 'all' ? searchedMedia : searchedMedia.filter(m => m.type === typeFilter)

    setMediaIds(filteredMedia.map(m => m.id))
  }, [media, search, typeFilter])

  async function handleRemove(id: string) {
    const response = await confirm('Delete file and remove from all models that use it?', {
      cancelText: 'No',
      okText: 'Remove',
    })
    if (!response) return false

    await deleteDoc('Media', id)
    const filtered = media.filter(file => file.id !== id)
    if (filtered.length !== media.length) setMedia(filtered)
    onRemove?.(id)
    return true
  }

  return (
    <LoaderCover flex={1} display="flex" show={loading}>
      <FileDrop
        flex={1}
        p={type ? 3 : 0}
        flexDirection="column"
        gap={2}
        options={{ accept: acceptedMediaFiles(type), disabled: noCreate }}
        onDrop={files => setNewMedia(files[0])}
      >
        {({ open }) => (
          <Flex direction="column" gap={4}>
            {!noCreate && (
              <Flex align="center" justify="space-between">
                <Flex maxW={isMobile ? '35%' : '250px'}>
                  <DebouncedTextField
                    ref={searchRef}
                    onChange={setSearch}
                    leftElement={<MagnifyingGlassIcon color="rgba(23, 23, 23, 0.6)" />}
                    placeholder="Search..."
                    value={search}
                  />
                </Flex>

                <Flex gap={2} align="center">
                  <ViewSelector options={['grid', 'list']} value={view} onChange={setView} />
                  <TypeSelector isDisabled={!!type} value={typeFilter} onChange={setTypeFilter} />
                  {isMobile ? (
                    <IconButton
                      aria-label="add-media"
                      icon={<PlusIcon />}
                      onClick={open}
                      variant="outline"
                    />
                  ) : (
                    <Button variant="outline" leftIcon={<PlusIcon />} onClick={open}>
                      Add media
                    </Button>
                  )}
                </Flex>
              </Flex>
            )}
            {mediaIds.length > 0 ? (
              <Flex direction="column" gap={2}>
                {view === 'list' && (
                  <Grid templateColumns={isMobile ? '40% 25% 25%' : '65% 20% 15%'} gap={3} px={3}>
                    <Text textStyle="bodySmall">Name</Text>
                    <Text textStyle="bodySmall">{isMobile ? 'By' : 'Updated by'}</Text>
                    <Text textStyle="bodySmall">{isMobile ? 'Date' : 'Updated'}</Text>
                  </Grid>
                )}
                <MediaGrid
                  templateColumns={
                    isMobile || view === 'list'
                      ? 'repeat(1, 1fr)'
                      : 'repeat(auto-fit, minmax(274px, 1fr))'
                  }
                  overflow="auto"
                  ids={mediaIds}
                  onRemove={handleRemove}
                  onSelect={onSelect}
                />
              </Flex>
            ) : (
              <Text textAlign="center" textStyle="h6" opacity={0.6}>
                No media found
              </Text>
            )}
          </Flex>
        )}
      </FileDrop>

      <MediaDialog
        open={Boolean(newMedia)}
        newMedia={newMedia}
        onClose={() => setNewMedia(undefined)}
        onSave={doc => {
          if (!onSelect)
            return setMedia(s => [...s, { id: doc.id, title: doc.title, type: doc.type }])
          onSelect(doc.id)
          setNewMedia(undefined)
        }}
      />
    </LoaderCover>
  )
}

interface TypeSelectorProps {
  isDisabled?: boolean
  value?: string
  onChange?: (value: string) => void
}

function TypeSelector({ isDisabled, value, onChange }: TypeSelectorProps) {
  const isMobile = useBreakpoint() < 2

  return (
    <Menu
      disabled={isDisabled}
      variant="solid"
      gap={{ base: 0, sm: 2 }}
      rightIcon={isMobile ? undefined : <ChevronDownIcon type="outline" />}
      buttonLabel={
        isMobile ? (
          <FunnelIcon type="outline" />
        ) : (
          <Text textStyle="bodySmall" fontSize={{ base: '0', sm: 'sm' }}>
            Type: {startCase(value) || 'All'}
          </Text>
        )
      }
      menuProps={{ matchWidth: true }}
      value={value || 'all'}
      onChange={onChange}
      options={filterOptions.map(option => ({ value: option.value, label: option.label }))}
    />
  )
}

const filterOptions = [
  { label: 'All', value: 'all' },
  { label: 'Image', value: 'image' },
  { label: 'Video', value: 'video' },
  { label: 'PDF', value: 'PDF' },
]
